import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import type { Language } from '@business-game/shared';
import { LanguageFlag } from './LanguageFlag';
import { SESSION_LANGUAGE_OPTIONS } from './sessionFormOptions';

type Props = {
  value: Language;
  onChange: (value: Language) => void;
  compact?: boolean;
  label?: string;
  className?: string;
};

export const SessionLanguagePicker: React.FC<Props> = ({
  value,
  onChange,
  compact = false,
  label,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = SESSION_LANGUAGE_OPTIONS.find((option) => option.value === value);

  useEffect(() => {
    if (!open || !triggerRef.current) return;

    const updateMenuPosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: rect.left,
        width: Math.max(rect.width, 168),
        zIndex: 200,
      });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const triggerClass = [
    'flex w-full items-center justify-between gap-2 rounded-[13px] border-[1.5px] border-slate-100 bg-white text-left transition-all',
    'focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10',
    open ? 'border-indigo-500 ring-4 ring-indigo-500/10' : 'hover:border-slate-200',
    compact ? 'h-[50px] px-[15px]' : 'px-5 py-4',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const flagClass = compact ? 'h-5 w-7' : 'h-6 w-9';

  return (
    <div ref={rootRef} className="relative">
      {label ? (
        <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">{label}</label>
      ) : null}

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? `Langue : ${selected.label}` : 'Choisir la langue'}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          {selected ? (
            <>
              <LanguageFlag
                countryCode={selected.countryCode}
                title={selected.label}
                className={`shrink-0 rounded-sm shadow-sm ring-1 ring-slate-200/80 ${flagClass}`}
              />
              <span className={`truncate font-bold text-slate-900 ${compact ? 'text-sm' : 'text-base'}`}>
                {selected.label}
              </span>
            </>
          ) : (
            <span className="text-sm font-bold text-slate-400">Choisir une langue</span>
          )}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              aria-label="Langues disponibles"
              style={menuStyle}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-200/80 ring-1 ring-slate-100"
            >
              <div className="grid grid-cols-3 gap-1.5">
                {SESSION_LANGUAGE_OPTIONS.map((language) => {
                  const isSelected = language.value === value;

                  return (
                    <button
                      key={language.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      title={language.label}
                      onClick={() => {
                        onChange(language.value);
                        setOpen(false);
                      }}
                      className={[
                        'flex items-center justify-center rounded-xl border-2 p-2 transition-all',
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200/60'
                          : 'border-transparent hover:border-slate-200 hover:bg-slate-50',
                      ].join(' ')}
                    >
                      <LanguageFlag
                        countryCode={language.countryCode}
                        title={language.label}
                        className={`rounded-sm shadow-sm ring-1 ring-slate-200/80 ${flagClass}`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};
